const pad2 = (n: number): string => String(n).padStart(2, '0');

const EARLIEST_BIRTH_DATE = '1900-01-01';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Built from the parts, not Date arithmetic, so 29 February minus a common
// year compares as 02-29 instead of rolling into March.
const localIsoDate = (now: Date, yearsBack = 0): string =>
  `${now.getFullYear() - yearsBack}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;

/**
 * Convert a masked US date of birth (MM/DD/YYYY, as collected by
 * MaskedDateOfBirthInput) to the ISO 8601 calendar date (YYYY-MM-DD) the CMS
 * Integration Service / FHIR Patient.birthDate expects.
 *
 * Returns '' for any value that is not a real calendar date in MM/DD/YYYY
 * form. Whether the date is plausible as a birth date is a separate question:
 * see isoBirthDateIsInRange.
 */
export const toIsoBirthDate = (mmddyyyy: string): string => {
  // Structural check: exactly MM/DD/YYYY with digits only. This is an exported
  // utility feeding a CMS FHIR field directly, so it must not assume the caller
  // (MaskedDateOfBirthInput) already sanitized the value.
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(mmddyyyy)) return '';
  const [month, day, year] = mmddyyyy.split('/');
  const m = Number(month);
  const d = Number(day);
  const y = Number(year);
  // Calendar check: Date rolls an impossible month or day (13/45, 02/31) into
  // another date, and maps years 0-99 to 1900-1999; reject all of those.
  const parsed = new Date(y, m - 1, d);
  if (parsed.getFullYear() !== y || parsed.getMonth() !== m - 1 || parsed.getDate() !== d) {
    return '';
  }
  return `${year}-${month}-${day}`;
};

/**
 * Whether an ISO calendar date (YYYY-MM-DD) is a plausible date of birth: not
 * before 1900 and strictly before today. "Today" is the member's local day,
 * not UTC, so a date typed late in the evening west of UTC is judged by the
 * date on their own calendar.
 */
export const isoBirthDateIsInRange = (iso: string, now: Date = new Date()): boolean => {
  if (!ISO_DATE.test(iso)) return false;
  return iso >= EARLIEST_BIRTH_DATE && iso < localIsoDate(now);
};

/**
 * Whether an ISO calendar date (YYYY-MM-DD) makes the member younger than 18
 * on their local calendar day. Only for a dismissable typo warning, never a
 * gate: Medicare also covers members under 65 through disability and ESRD.
 */
export const isoBirthDateIsUnder18 = (iso: string, now: Date = new Date()): boolean => {
  if (!ISO_DATE.test(iso)) return false;
  return iso > localIsoDate(now, 18);
};
